/* eslint-disable jsx-a11y/anchor-is-valid */
import React from 'react';
import '../css/floating-search-input.sass';
import TextInput from './TextInput';
import SearchSuggestions from './SearchSuggestions';

interface FloatingSearchInputProps {
  searchInputLabel?: string;
  iconSrc?: string;
  searchBtnTitle?: string;
  name: string;
  value?: string;
  id?: string;
  required?: boolean;
  focus?: boolean;
  hasError?: boolean;
  errors?: any;
  theme?: 'night' | 'day';
  maxLength?: number;
  onChange?: Function;
  onError?: Function;
  onBlur?: Function;
  onSearch?: Function;
  onClear?: Function;
  onSuggestion?: Function;
  onLeft?: Function;
  onRight?: Function;
  onSpace?: Function;
  autoSuggest?: boolean;
  suggestOn?: number;
  suggestionsList?: string[];
  maxSuggestions?: number;
  disabled?: boolean;
  toolTip?: React.ReactNode;
  autocomplete?: string;
  queryParam?: string;
  handleClose: Function;
  isActive?: boolean;
  closeIconRef?: any;
}

interface SearchInputState {
  inputValue: string;
  displaySearchSuggestions: boolean;
  selectedSuggestionIndex: number;
  searchValue: string;
}

export default function FloatingSearchInput(props: FloatingSearchInputProps) {
  const {
    suggestOn = 3,
    maxSuggestions = 5,
    id = 'searchInput',
    theme = 'day',
    searchInputLabel = 'Search',
    maxLength = 80,
    searchBtnTitle = 'Search DISH',
    iconSrc = '/cms-files/common/icon-search_03.png',
    queryParam = '/search?q='
  } = props;

  const [state, setState] = React.useState<SearchInputState>({
    searchValue: props.value || '',
    displaySearchSuggestions: false,
    selectedSuggestionIndex: -1,
    inputValue: ''
  });
  const suggestions =
    props.suggestionsList && props.suggestionsList.slice(0, maxSuggestions);

  return (
    <>
      <div
        className={`floating-search-input ${props.isActive ? 'isActive' : ''}`}
      >
        <label htmlFor={id} className="searchInputLabel">
          {searchInputLabel}
        </label>
        <TextInput
          label={searchInputLabel}
          floatingSearchClass={props.isActive ? 'isActive' : ''}
          hideLabel
          hideSearchIcon
          floatingSearch
          inputMode="search"
          name={props.name}
          value={state.searchValue}
          id={id}
          required={props.required}
          focus={props.focus}
          hasError={props.hasError}
          errors={props.errors}
          theme={theme}
          maxLength={maxLength}
          type="search"
          onError={props.onError}
          autocomplete={props.autocomplete}
          onBlur={(event: any) => {
            setState({
              ...state,
              displaySearchSuggestions: false
            });
            if (props.onBlur !== undefined) {
              props.onBlur(event);
            }
          }}
          onChange={(event: any) => {
            let displaySearchSuggestions = false;
            if (props.autoSuggest && event.target.value.length >= suggestOn) {
              if (props.onSuggestion !== undefined) {
                props.onSuggestion(event.target.value);
              }
              displaySearchSuggestions = true;
            }
            setState({
              ...state,
              searchValue: event.target.value,
              displaySearchSuggestions,
              inputValue: event.target.value
            });
            if (props.onChange !== undefined) {
              props.onChange({
                ...event,
                target: { ...event.target }
              });
            }
          }}
          onEsc={(inputValue: string) => {
            if (props.onClear !== undefined) {
              props.onClear(inputValue);
            }
            setState({
              ...state,
              searchValue: '',
              displaySearchSuggestions: false
            });
          }}
          onEnter={(inputValue: string) => {
            if (props.onSearch !== undefined) {
              props.onSearch(inputValue.replace(/(^['"]+|['"]+$)/g, ''));
            }
            setState({
              ...state,
              searchValue: inputValue,
              displaySearchSuggestions: false
            });
          }}
          onUp={() => {
            if (suggestions) {
              let { selectedSuggestionIndex } = state;
              selectedSuggestionIndex += -1;

              if (selectedSuggestionIndex > suggestions.length - 1) {
                selectedSuggestionIndex = -1;
              } else if (selectedSuggestionIndex < -1) {
                selectedSuggestionIndex = suggestions.length - 1;
              }

              const value =
                selectedSuggestionIndex > -1
                  ? suggestions[selectedSuggestionIndex]
                  : state.inputValue;
              if (selectedSuggestionIndex !== state.selectedSuggestionIndex) {
                setState({
                  ...state,
                  selectedSuggestionIndex,
                  searchValue: value
                });
              }
            }
          }}
          onDown={() => {
            if (suggestions) {
              let { selectedSuggestionIndex } = state;
              selectedSuggestionIndex += 1;

              if (selectedSuggestionIndex > suggestions.length - 1) {
                selectedSuggestionIndex = -1;
              } else if (selectedSuggestionIndex < -1) {
                selectedSuggestionIndex = suggestions.length - 1;
              }

              const value =
                selectedSuggestionIndex > -1
                  ? suggestions[selectedSuggestionIndex]
                  : state.inputValue;
              if (selectedSuggestionIndex !== state.selectedSuggestionIndex) {
                setState({
                  ...state,
                  selectedSuggestionIndex,
                  searchValue: value
                });
              }
            }
          }}
          onLeft={props.onLeft}
          onRight={props.onRight}
          onSpace={props.onSpace}
          disabled={props.disabled}
          toolTip={props.toolTip}
        />

        <button
          tabIndex={props.focus ? 0 : -1}
          type="button"
          id="searchSubmit"
          className="floatingSearchBtn"
          onClick={() => {
            if (props.onSearch !== undefined && state.searchValue) {
              props.onSearch(state.searchValue);
            }
          }}
        >
          <img src={iconSrc} alt="search icon" title={searchBtnTitle} />
        </button>

        <button
          tabIndex={props.focus ? 0 : -1}
          className="searchBtnClose"
          ref={props.closeIconRef}
          type="button"
          onClick={() => {
            if (props.isActive) {
              if (props.onClear !== undefined) {
                props.onClear();
              }
              setState({ ...state, searchValue: '' });
              props.handleClose();
            }
          }}
        >
          <span className="active">Close</span>
        </button>
      </div>
      <div className="floating-search-suggestion">
        {state.displaySearchSuggestions &&
          suggestions &&
          suggestions.length > 0 && (
            <div
              id={`${state.displaySearchSuggestions ? 'searchResults' : ''}`}
              aria-hidden="false"
              className="searchSuggestionList"
            >
              <SearchSuggestions
                suggestions={suggestions || []}
                floatingSearch
                queryParam={queryParam}
                selectedSuggestionIndex={state.selectedSuggestionIndex}
                onClick={(e: any) => {
                  setState({
                    ...state,
                    searchValue: e.target.selectedValue,
                    displaySearchSuggestions: false
                  });
                  window.open(
                    window.location.origin.concat(queryParam) +
                      e.target.selectedValue,
                    '_self'
                  );
                }}
              />
            </div>
          )}
      </div>
    </>
  );
}
